/**
 * useOnboarding Hook
 * Global state management for onboarding flow
 */

import { create } from 'zustand';
import { supabase } from '../../../lib/supabase';

interface OnboardingStore {
  isOpen: boolean;
  hasSeenOnboarding: boolean;
  openOnboarding: () => void;
  closeOnboarding: () => void;
  markAsComplete: (userId: string) => Promise<void>;
  checkIfNewUser: (userId: string) => Promise<boolean>;
  setHasSeen: (value: boolean) => void;
}

export const useOnboarding = create<OnboardingStore>((set, get) => ({
  isOpen: false,
  hasSeenOnboarding: false,

  openOnboarding: () => {
    set({ isOpen: true });
    // Lock body scroll
    document.body.style.overflow = 'hidden';
  },

  closeOnboarding: () => {
    set({ isOpen: false });
    // Unlock body scroll
    document.body.style.overflow = 'unset';
  },

  setHasSeen: (value: boolean) => {
    set({ hasSeenOnboarding: value });
  },

  markAsComplete: async (userId: string) => {
    // Always save to localStorage as primary storage
    localStorage.setItem('swirl_onboarding_completed', 'true');
    set({ hasSeenOnboarding: true });

    // Try to save to Supabase as secondary backup (non-blocking)
    try {
      const { error } = await supabase
        .schema('public')
        .from('user_profiles')
        .upsert({
          id: userId,
          has_completed_onboarding: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        });

      if (error) {
        console.warn('Failed to save onboarding status to Supabase (non-critical):', error);
      }
    } catch (err) {
      console.warn('Error saving onboarding status to Supabase (non-critical):', err);
    }
  },

  checkIfNewUser: async (userId: string) => {
    // Check localStorage first (faster and more reliable)
    const localCompleted = localStorage.getItem('swirl_onboarding_completed');
    if (localCompleted === 'true') {
      set({ hasSeenOnboarding: true });
      return false; // Not a new user
    }

    // Check Supabase as backup
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('user_profiles')
        .select('has_completed_onboarding')
        .eq('id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116' && error.code !== 'PGRST205') {
        console.warn('Error checking onboarding status:', error);
      }

      const hasCompleted = data?.has_completed_onboarding === true;
      set({ hasSeenOnboarding: hasCompleted });
      
      // Sync to localStorage
      if (hasCompleted) {
        localStorage.setItem('swirl_onboarding_completed', 'true');
      }

      return !hasCompleted; // Return true if new user
    } catch (err) {
      console.warn('Error checking onboarding status:', err);
      // Default to false (not a new user) on error
      return false;
    }
  }
}));

